import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  fetchHostedReport,
  fetchReportPreview,
  scanMapUrl,
} from '../api/reportApi';
import { AccountNotes } from '../components/AccountNotes';
import AgencyBar from '../components/AgencyBar';
import ReportFooter from '../components/ReportFooter';
import ReportIntro from '../components/ReportIntro';
import {
  CitationsSection,
  KpiGrid,
  MapPackSection,
  SearchSection,
  Section,
  WorkSection,
} from '../components/ReportSections';
import WeekNavigator from '../components/WeekNavigator';
import {
  CARD_BORDER,
  DEFAULT_BRAND,
  FONT,
  PAGE_BG,
  WHITE,
  cardSx,
  sectionSubtitleSx,
} from '../reportTokens';
import { reportLoadFailure, tagLoadedReport, tagReportRoute } from '../sentry';
import type { HostedReport } from '../types/report.types';

/**
 * The hosted weekly report.
 *
 * Read by the agency's CLIENT, from a link in an email — so there is no
 * navigation, no sign-in and nothing to click away to. A section for a
 * service the business has is always drawn, even in a quiet week, with an
 * empty state saying why: one that vanished some weeks would read as a report
 * that broke. A section for a service it does not have — map tracking never
 * switched on, citations not in the plan, Search Console not connected — is
 * left out. "Not part of this plan yet" in front of a client advertises what
 * they are not paying for.
 */
/**
 * A section the client sees only when `shown`.
 *
 * In the agency's preview a hidden section is still drawn, faded, under a
 * note saying the client will not see it and why. Otherwise the agency is
 * left wondering where Citation health went, and has no way to learn what
 * switching the service on would add.
 */
function ClientOnlyIf({
  shown,
  preview,
  reason,
  children,
}: {
  shown: boolean;
  preview: boolean;
  reason: string;
  children: ReactNode;
}) {
  if (shown) return <>{children}</>;
  if (!preview) return null;
  return (
    <Box className='report-no-print'>
      <Typography
        role='note'
        sx={{ ...sectionSubtitleSx, mb: 1, fontWeight: 600 }}
      >
        Hidden from your client: {reason} It appears once there is data to show.
      </Typography>
      <Box sx={{ opacity: 0.55 }}>{children}</Box>
    </Box>
  );
}

/** The longest Download PDF waits for images before printing anyway. */
const PRINT_ASSET_WAIT_MS = 8000;

function ReportPage({ preview = false }: { preview?: boolean }) {
  const { shareToken, previewToken } = useParams<{
    shareToken: string;
    previewToken: string;
  }>();
  const token = preview ? previewToken : shareToken;
  // `?print=1` is how Download PDF arrives. The browser's own print-to-PDF is
  // what produces the file: it gives selectable vector text and honours the
  // print stylesheet below, where a server-side headless Chrome would cost a
  // 300MB dependency and a buildpack to produce something worse.
  const [searchParams] = useSearchParams();
  const printRequested = searchParams.get('print') === '1';
  const [report, setReport] = useState<HostedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  // The design's Client / Agency switch. Only offered when the payload says the
  // reader may see the agency view — the public route always says no, and
  // strips the notes besides, so this cannot become a way to reveal them.
  const [agencyView, setAgencyView] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }
    tagReportRoute(token, preview);
    (preview ? fetchReportPreview(token) : fetchHostedReport(token))
      .then(result => {
        if (cancelled) return;
        tagLoadedReport(result, token);
        setReport(result);
      })
      .catch(error => {
        if (cancelled) return;
        reportLoadFailure(error);
        setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, preview]);

  useEffect(() => {
    if (!printRequested || loading || !report) return undefined;
    let cancelled = false;
    let printTimer: number | undefined;

    /**
     * Print once the page's images and fonts have arrived, not after a fixed
     * delay. The agency logo and the post thumbnails are fetched only after
     * the report JSON lands, and on a cold cache or a slow connection the
     * dialog used to open first — a PDF with empty boxes where the logo
     * goes, and the layout reflowing under the snapshot.
     *
     * Capped, so one image that never answers cannot hold the dialog back
     * for ever; a failed image settles as done, like a loaded one.
     *
     * Lazy images are switched to eager first. Below the fold they never
     * start loading on their own, so the wait always ran to its cap and the
     * citation directory logos could print blank.
     */
    document
      .querySelectorAll<HTMLImageElement>('img[loading="lazy"]')
      .forEach(image => image.setAttribute('loading', 'eager'));
    const pending = Array.from(document.images)
      .filter(image => !image.complete)
      .map(
        image =>
          new Promise<void>(resolve => {
            image.addEventListener('load', () => resolve(), { once: true });
            image.addEventListener('error', () => resolve(), { once: true });
          }),
      );
    const fontsReady = document.fonts?.ready ?? Promise.resolve();
    const cap = new Promise<void>(resolve => {
      window.setTimeout(resolve, PRINT_ASSET_WAIT_MS);
    });

    void Promise.race([Promise.all([...pending, fontsReady]), cap]).then(() => {
      if (cancelled) return;
      // One frame after the last asset, so its layout has been painted.
      printTimer = window.setTimeout(() => window.print(), 300);
    });

    return () => {
      cancelled = true;
      if (printTimer !== undefined) window.clearTimeout(printTimer);
    };
  }, [printRequested, loading, report]);

  const accent = report?.branding?.primaryColor || DEFAULT_BRAND;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: PAGE_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (notFound || !report) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: PAGE_BG,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
        }}
      >
        <Box sx={{ ...cardSx, maxWidth: 440, textAlign: 'center' }}>
          <Typography sx={{ fontSize: FONT.cardTitle, fontWeight: 700 }}>
            This report is not available
          </Typography>
          {/* Deliberately one message for every reason — wrong token, expired
              link, a report sent before hosted reports existed. Telling them
              apart would let someone walk the tokens. */}
          <Typography sx={{ ...sectionSubtitleSx, mt: 1 }}>
            The link may be incorrect, or the report may no longer be shared.
            Ask whoever sent it for a fresh link.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: PAGE_BG,
        pb: 8,
        '@media print': {
          bgcolor: WHITE,
          pb: 0,
          // Interactive chrome is not part of the document.
          '& .report-no-print': { display: 'none' },
          // A section split across a page break is unreadable, and a heading
          // stranded at the foot of a page is worse.
          '& .report-section': {
            breakInside: 'avoid',
            pageBreakInside: 'avoid',
            border: `1px solid ${CARD_BORDER}`,
            boxShadow: 'none',
          },
        },
      }}
    >
      <Box
        sx={{
          // The design's column: 1080px with 24px gutters. Wider than the
          // 960 this had, which is what made every section feel cramped
          // against the reference.
          maxWidth: 1080,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          pt: { xs: 3, sm: 5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {report.isPreview && (
          <Box
            className='report-no-print'
            role='status'
            sx={{
              ...cardSx,
              py: 1.5,
              bgcolor: '#FFFBEB',
              borderColor: '#FCD34D',
            }}
          >
            <Typography sx={{ fontSize: FONT.body, fontWeight: 700 }}>
              Preview
            </Typography>
            <Typography sx={{ ...sectionSubtitleSx, mt: 0.25 }}>
              This is what your client will see. Nothing has been sent, and this
              link stops working within the hour.
            </Typography>
          </Box>
        )}
        <AgencyBar
          report={report}
          agencyView={agencyView}
          onAgencyViewChange={setAgencyView}
          accent={accent}
        />
        <WeekNavigator weeks={report.weeks} accent={accent} />
        <ReportIntro report={report} />

        {report.kpis.length > 0 ? (
          // No wrapping card: each KPI carries its own, as the design draws
          // them. Inside one frame the four figures read as a table.
          <Box className='report-section'>
            <KpiGrid kpis={report.kpis} />
          </Box>
        ) : (
          <Section title='Profile performance'>
            <Typography sx={sectionSubtitleSx}>
              Google Business Profile metrics are not available for this
              business yet.
            </Typography>
          </Section>
        )}

        {/* Every section renders every week. One that vanishes when a company
            has no data reads as a report that broke — each says what it has,
            or says plainly that it has nothing. */}
        <WorkSection
          rows={report.work}
          total={report.workTotal}
          cards={report.workCards}
          feed={report.feed}
          upcoming={report.upcomingWork}
        />
        {/* Tracking that is on but had no scan this week, or failed to
            load, still shows: those are about this week, not the plan. */}
        {/* Left out entirely when the agency turned map rankings off for
            this client, whatever the tracking state says. */}
        <ClientOnlyIf
          shown={Boolean(
            !report.mapPack.hiddenByAgency &&
              (report.mapPack.enabled ||
                report.mapPack.trackingOn ||
                report.mapPack.loadError),
          )}
          preview={Boolean(report.isPreview)}
          reason={
            report.mapPack.hiddenByAgency
              ? 'map rankings are turned off in this report’s settings.'
              : 'map tracking is not switched on for this business.'
          }
        >
          <MapPackSection
            mapPack={report.mapPack}
            agencyView={agencyView}
            mapUrl={
              token
                ? (index, side) => scanMapUrl(token, preview, index, side)
                : undefined
            }
          />
        </ClientOnlyIf>
        <ClientOnlyIf
          shown={report.citations.enabled}
          preview={Boolean(report.isPreview)}
          reason='citation management is not part of this plan.'
        >
          <CitationsSection citations={report.citations} />
        </ClientOnlyIf>
        {/* `enabled` is false without Search Console rows, which is no
            connection or one with nothing to show; either way, nothing. */}
        <ClientOnlyIf
          shown={report.search.enabled}
          preview={Boolean(report.isPreview)}
          reason='Search Console is not connected, or has no data for this week.'
        >
          <SearchSection
            search={report.search}
            preview={Boolean(report.isPreview)}
          />
        </ClientOnlyIf>
        <AccountNotes
          notes={report.notes}
          canViewAgency={report.canViewAgency}
          agencyView={agencyView}
        />

        <ReportFooter report={report} />
      </Box>
    </Box>
  );
}

export default ReportPage;
